import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, User, Building2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { FadeUp } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["AI", "SEO", "Travel", "Social", "Real Estate", "Marketing", "Others"];
const REGIONS = ["US", "UK", "India", "Global"];

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#121210] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-emerald-500/50 focus:shadow-[0_0_15px_rgba(16,185,129,0.1)] transition-all duration-200";

const labelCls = "block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-300 mb-2 font-semibold";

export default function AddBusiness() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  const [step, setStep] = useState(1); // 1 = business, 2 = account (skipped if logged in)
  const [biz, setBiz] = useState({ name: "", category: "", region: "", tagline: "", website: "", description: "" });
  const [account, setAccount] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const setBizField = (k) => (e) => setBiz({ ...biz, [k]: e.target.value });
  const setAccountField = (k) => (e) => setAccount({ ...account, [k]: e.target.value });

  const handleStep1 = (e) => {
    e.preventDefault();
    setError("");
    // If already logged in, submit immediately
    if (user) {
      submitListing();
    } else {
      setStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const submitListing = async (registeredUser = null) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/companies", biz);
      if (registeredUser || user) {
        toast.success(`${data.name} submitted — we'll review it shortly.`);
      } else {
        toast.success(`${data.name} is on the board.`);
      }
      navigate(`/company/${data.slug}`);
    } catch (err) {
      setError(formatApiError(err));
      setSubmitting(false);
    }
  };

  const handleStep2 = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      // 1. Register account
      const { data: newUser } = await api.post("/auth/register", account);
      setUser(newUser);
      // 2. Create listing (uses the new session cookie)
      await submitListing(newUser);
    } catch (err) {
      setError(formatApiError(err));
      setSubmitting(false);
    }
  };

  const totalSteps = user ? 1 : 2;

  return (
    <main data-testid="add-business-page" className="pt-28 md:pt-16 min-h-screen">
      <Seo
        title="List your startup — HighRank"
        siteName="HighRank"
        description="Add your startup or small business to the HighRank leaderboard."
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute -top-24 left-1/3 w-96 h-96 rounded-full bg-emerald-500/10 blur-[110px]" />

        <div className="relative max-w-2xl mx-auto px-4 sm:px-6 py-12">
          <FadeUp>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-emerald-400 mb-3">
              {"// Free during launch"}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-wide text-slate-100 mb-3">
              {step === 2 ? "Create account" : "List your business"}
            </h1>
            <p className="text-sm sm:text-base text-slate-400 leading-relaxed mb-8">
              {step === 2
                ? "One account gives you reviews, endorsements, and a live rank. No spam."
                : "Get a public profile, a Rank Position and a HighRank Score that grows with real reviews."}
            </p>
          </FadeUp>

          {/* Step indicator — only shown for guests */}
          {!user && (
            <FadeUp delay={0.05}>
              <div className="flex items-center gap-3 mb-8">
                {[
                  { n: 1, icon: Building2, label: "Business" },
                  { n: 2, icon: User, label: "Account" },
                ].map(({ n, icon: Icon, label }, i) => (
                  <div key={n} className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                        step > n
                          ? "bg-emerald-500 text-black"
                          : step === n
                          ? "bg-emerald-500/20 border border-emerald-500/60 text-emerald-400"
                          : "bg-white/5 border border-white/10 text-slate-600"
                      }`}>
                        {step > n ? <CheckCircle2 size={14} /> : n}
                      </div>
                      <span className={`text-xs font-medium hidden sm:block ${step === n ? "text-slate-300" : "text-slate-600"}`}>
                        {label}
                      </span>
                    </div>
                    {i < 1 && (
                      <div className={`flex-1 h-px w-12 ${step > 1 ? "bg-emerald-500/40" : "bg-white/10"}`} />
                    )}
                  </div>
                ))}
              </div>
            </FadeUp>
          )}

          {/* Step 1 — Business Details */}
          {step === 1 && (
            <FadeUp delay={0.1}>
              <form onSubmit={handleStep1} data-testid="add-business-form" className="rounded-2xl border border-white/[0.07] bg-[#0A0A09] p-7 sm:p-9 space-y-5">

                {user && (
                  <div className="flex items-center gap-2 text-xs text-slate-500 bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2.5">
                    <User size={13} className="text-emerald-400 shrink-0" />
                    Listing as <span className="text-slate-300 font-medium">{user.name || user.email}</span>
                  </div>
                )}

                <div>
                  <label className={labelCls}>Company name <span className="text-rose-500">*</span></label>
                  <input data-testid="add-name-input" value={biz.name} onChange={setBizField("name")} required minLength={2} placeholder="e.g. NeuralForge" className={inputCls} />
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls}>Category <span className="text-rose-500">*</span></label>
                    <select data-testid="add-category-select" value={biz.category} onChange={setBizField("category")} required className={inputCls}>
                      <option value="" disabled>Select category</option>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Region <span className="text-rose-500">*</span></label>
                    <select data-testid="add-region-select" value={biz.region} onChange={setBizField("region")} required className={inputCls}>
                      <option value="" disabled>Select region</option>
                      {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Tagline</label>
                  <input data-testid="add-tagline-input" value={biz.tagline} onChange={setBizField("tagline")} maxLength={140} placeholder="One sharp line about what you do" className={inputCls} />
                </div>

                <div>
                  <label className={labelCls}>Website <span className="text-red-400">*</span></label>
                  <input data-testid="add-website-input" value={biz.website} onChange={setBizField("website")} type="url" required placeholder="https://yourcompany.com" className={inputCls} />
                </div>

                <div>
                  <label className={labelCls}>Description <span className="text-red-400">*</span></label>
                  <textarea data-testid="add-description-input" value={biz.description} onChange={setBizField("description")} rows={4} maxLength={1000} required placeholder="What do you build, who is it for, why does it win?" className={`${inputCls} resize-none`} />
                  <p className="mt-2 text-[11px] text-slate-600">A complete profile feeds 20% of your HighRank Score.</p>
                </div>

                {error && <p data-testid="add-form-error" className="text-sm text-red-400">{error}</p>}

                <button
                  type="submit"
                  data-testid="add-submit-button"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-bold rounded-full py-3.5 transition-all duration-200 hover:shadow-[0_0_25px_rgba(16,185,129,0.4)]"
                >
                  {submitting ? "Listing…" : user ? "Put me on the board" : "Continue to account"} <ArrowRight size={16} />
                </button>

                {!user && (
                  <p className="text-center text-xs text-slate-600">
                    Already have an account?{" "}
                    <Link to="/auth" className="text-emerald-400 hover:text-emerald-300 transition-colors">Sign in</Link>
                  </p>
                )}
              </form>
            </FadeUp>
          )}

          {/* Step 2 — Account Creation */}
          {step === 2 && !user && (
            <FadeUp delay={0.1}>
              <form onSubmit={handleStep2} data-testid="add-account-form" className="rounded-2xl border border-white/[0.07] bg-[#0A0A09] p-7 sm:p-9 space-y-5">

                {/* Business summary recap */}
                <div className="flex items-center gap-3 bg-emerald-500/[0.06] border border-emerald-500/20 rounded-xl px-4 py-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0">
                    <Building2 size={14} className="text-emerald-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-200 truncate">{biz.name}</p>
                    <p className="text-xs text-slate-500 truncate">{biz.category} · {biz.region}{biz.tagline ? ` · ${biz.tagline}` : ""}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setStep(1); setError(""); }}
                    className="ml-auto text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1 shrink-0 transition-colors"
                  >
                    <ChevronLeft size={13} /> Edit
                  </button>
                </div>

                <div>
                  <label className={labelCls}>Your name <span className="text-rose-500">*</span></label>
                  <input data-testid="account-name-input" value={account.name} onChange={setAccountField("name")} required minLength={2} placeholder="Your name" className={inputCls} />
                </div>

                <div>
                  <label className={labelCls}>Email <span className="text-rose-500">*</span></label>
                  <input data-testid="account-email-input" type="email" value={account.email} onChange={setAccountField("email")} required placeholder="you@company.com" className={inputCls} />
                </div>

                <div>
                  <label className={labelCls}>Password <span className="text-rose-500">*</span></label>
                  <input data-testid="account-password-input" type="password" value={account.password} onChange={setAccountField("password")} required minLength={8} placeholder="8+ characters" className={inputCls} />
                </div>

                {error && <p data-testid="add-account-error" className="text-sm text-red-400">{error}</p>}

                <button
                  type="submit"
                  data-testid="add-account-submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-bold rounded-full py-3.5 transition-all duration-200 hover:shadow-[0_0_25px_rgba(16,185,129,0.4)]"
                >
                  {submitting ? "Creating your listing…" : "Create account & list business"} <ArrowRight size={16} />
                </button>

                <p className="text-center text-xs text-slate-600">
                  Already have an account?{" "}
                  <Link to="/auth" state={{ from: "/add-business" }} className="text-emerald-400 hover:text-emerald-300 transition-colors">Sign in first</Link>
                </p>
              </form>
            </FadeUp>
          )}
        </div>
      </section>
    </main>
  );
}
